import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCurrentUserTypeRoles } from '../../util/userHelpers';
import {
    fetchItineraryEvents as fetchItineraryEventsApi,
    fetchProviderItineraryEvents as fetchProviderItineraryEventsApi,
    dbEventsToCalendarEvents,
    providerEventsToCalendarEvents
} from '../../util/favoriteUtils';
import { getProviderDashboard as getProviderDashboardApi } from '../../util/api';
import { getProcess } from '../../transactions/transaction';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { transactionLineItems } from '../../util/api';
import * as log from '../../util/log';

const { UUID } = sdkTypes;

/**
 * An id the SDK will recognise as one.
 *
 * The ids on this page do not come from the SDK: a booking's transaction and a listing's
 * id are read out of the itinerary's own records, where they are strings. The SDK writes
 * a UUID instance as a tagged value and a string as a string, and the API wants the tag —
 * so every id handed to it from here is put back into one first. Anything already a UUID
 * is left alone, and anything shaped like one — { uuid } off a stored snapshot — is
 * rebuilt rather than passed through as a plain object.
 */
const asUUID = id => {
    if (!id) return null;
    if (id instanceof UUID) return id;
    if (typeof id === 'string') return new UUID(id);
    return id.uuid ? new UUID(id.uuid) : null;
};
import moment from 'moment';

// ================ Async Thunks ================ //

/**
 * Has this account ever put an experience on the site?
 *
 * That, rather than the type on the profile, is what entitles somebody to the provider's
 * half of this page. Both of this marketplace's user types may post listings, so a
 * Customer who runs experiences is as much a provider as a Supplier is, and asking the
 * profile would shut them out of their own bookings.
 *
 * Every state counts — a draft never finished, a listing closed years ago — because the
 * question is whether they have ever been a provider, not whether they are selling today.
 * A listing deleted outright is the one thing this cannot see.
 */
/**
 * What a booking would come to, asked of the same endpoint the listing page asks.
 *
 * The popup prices a booking before the guest leaves for the payment page, and the figure
 * it shows has to be the one they are charged. Asking the server rather than multiplying
 * a price by a seat count is the whole point: the commission, any discount and the
 * provider's own rules live in the line items, and arithmetic here would be a second
 * opinion that drifts.
 *
 * In the itinerary's own slice rather than the listing page's, which is where this
 * endpoint's answers normally land. A guest opening this popup may have visited a listing
 * earlier in the session, and that slice would still be holding its line items — so the
 * popup would price one session with another's figures until the fetch came back. Money
 * that is briefly wrong is worse than money that is briefly missing.
 */
const fetchLineItemsPayloadCreator = ({ orderData, listingId, isOwnListing }, { rejectWithValue }) =>
    transactionLineItems({ orderData, listingId, isOwnListing })
        .then(response => response.data)
        .catch(e => {
            log.error(e, 'itinerary-fetching-line-items-failed', {
                listingId: listingId?.uuid,
                orderData,
                statusText: e.statusText,
            });
            return rejectWithValue(storableError(e));
        });

export const fetchLineItemsThunk = createAsyncThunk(
    'ItineraryPage/fetchTransactionLineItems',
    fetchLineItemsPayloadCreator
);

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch =>
    dispatch(fetchLineItemsThunk({ orderData, listingId, isOwnListing })).unwrap();

/**
 * The listing itself, put where everything else expects to find it.
 *
 * The itinerary holds a snapshot of a listing, not the listing: enough to draw a card,
 * and nowhere near enough to take money. The checkout page wants the entity — its
 * price, its type, its author — and reads it out of the shared marketplaceData store,
 * which nothing on this page was ever filling.
 *
 * So the first press of Book now found no listing, fell through the guard in
 * handleBookSlot, and went to the listing page instead of to checkout. The listing
 * page then loaded the entity, and every press after that worked. "It doesn't work the
 * first time and works every time after" was that, exactly: the fallback was doing the
 * loading, one navigation too late to be of use to the press that triggered it.
 *
 * The denormalised entity is returned as well as stored, because a caller inside an
 * async handler is holding a getListing closed over the state as it was before this
 * dispatch, and would read null from it however well the store had been filled.
 *
 * The include list and image variants match ListingPage's, so the entity handed to
 * checkout from here is the same one it would have been handed from there — the side
 * card looks for variants named after the configured prefix, and an entity fetched
 * without them shows a listing with no picture.
 */
const fetchListingPayloadCreator = async (
    { listingId, config },
    { dispatch, getState, extra: sdk, rejectWithValue }
) => {
    try {
        const id = asUUID(listingId);
        if (!id) throw new Error('No listing to fetch');

        const {
            aspectWidth = 1,
            aspectHeight = 1,
            variantPrefix = 'listing-card',
        } = config?.layout?.listingImage || {};
        const aspectRatio = aspectHeight / aspectWidth;

        const response = await sdk.listings.show({
            id,
            include: ['author', 'author.profileImage', 'images', 'currentStock'],
            'fields.image': [
                `variants.${variantPrefix}`,
                `variants.${variantPrefix}-2x`,
                'variants.square-small',
                'variants.square-small2x',
            ],
            ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio, 'scale'),
            ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio, 'scale'),
        });

        dispatch(
            addMarketplaceEntities(response, { listingFields: config?.listing?.listingFields })
        );

        const [listing] = getMarketplaceEntities(getState(), [{ id, type: 'listing' }]);
        return listing || null;
    } catch (e) {
        return rejectWithValue(storableError(e));
    }
};

export const fetchListingThunk = createAsyncThunk(
    'ItineraryPage/fetchListing',
    fetchListingPayloadCreator
);

export const fetchListing = (listingId, config) => dispatch =>
    dispatch(fetchListingThunk({ listingId, config })).unwrap();

export const fetchHasListedThunk = createAsyncThunk(
    'ItineraryPage/fetchHasListed',
    async (_, { extra: sdk, rejectWithValue }) => {
        try {
            // One listing is as good an answer as a hundred: only the count is read.
            const response = await sdk.ownListings.query({ page: 1, perPage: 1 });
            return (response?.data?.meta?.totalItems || 0) > 0;
        } catch (e) {
            return rejectWithValue(storableError(e));
        }
    }
);

export const fetchHasListed = () => dispatch => dispatch(fetchHasListedThunk());

export const fetchItineraryEventsThunk = createAsyncThunk(
    'ItineraryPage/fetchEvents',
    async ({ userId, start, end, isProvider }, { rejectWithValue }) => {
        try {
            // Always fetch the user's own events (events they saved as a customer)
            const customerEvents = await fetchItineraryEventsApi(userId, start, end);
            const customerCalendarEvents = dbEventsToCalendarEvents(customerEvents);

            // If user is a provider, also fetch events where customers favorited their listings
            if (isProvider) {
                const providerEvents = await fetchProviderItineraryEventsApi(userId, start, end);
                const providerCalendarEvents = providerEventsToCalendarEvents(providerEvents);
                
                // Combine both sets of events
                return [...customerCalendarEvents, ...providerCalendarEvents];
            }

            return customerCalendarEvents;
        } catch (e) {
            return rejectWithValue(storableError(e));
        }
    }
);

// Fetch customer events only (events the user saved as a customer)
export const fetchCustomerEventsThunk = createAsyncThunk(
    'ItineraryPage/fetchCustomerEvents',
    async ({ userId, start, end }, { rejectWithValue }) => {
        try {
            const customerEvents = await fetchItineraryEventsApi(userId, start, end);
            return dbEventsToCalendarEvents(customerEvents);
        } catch (e) {
            return rejectWithValue(storableError(e));
        }
    }
);

// Fetch seller events only (customers who favorited the user's listings)
export const fetchSellerEventsThunk = createAsyncThunk(
    'ItineraryPage/fetchSellerEvents',
    async ({ userId, start, end }, { rejectWithValue }) => {
        try {
            const providerEvents = await fetchProviderItineraryEventsApi(userId, start, end);
            return providerEventsToCalendarEvents(providerEvents);
        } catch (e) {
            return rejectWithValue(storableError(e));
        }
    }
);

// ================ Provider dashboard ================ //

// The provider tab is a dashboard rather than a calendar of saved events: it needs
// capacity, payout, booking requests and interest counts, which only the dashboard
// endpoint joins together. See `server/api/providerDashboard.js`.
export const fetchProviderDashboardThunk = createAsyncThunk(
    'ItineraryPage/fetchProviderDashboard',
    async ({ userId }, { rejectWithValue }) => {
        try {
            return await getProviderDashboardApi(userId);
        } catch (e) {
            return rejectWithValue(storableError(e));
        }
    }
);

// Answering a guest's booking request is an ordinary Sharetribe transition, so it goes
// through the marketplace SDK as the provider. The dashboard is refetched afterwards
// rather than patched locally, so what the page shows is what the API holds.
const transitionRequest = transition =>
    createAsyncThunk(
        `ItineraryPage/${transition}`,
        async ({ txId, protectedData }, { rejectWithValue, extra: sdk }) => {
            try {
                await sdk.transactions.transition({
                    id: asUUID(txId),
                    transition,
                    params: protectedData ? { protectedData } : {},
                });
                return { txId };
            } catch (e) {
                return rejectWithValue(storableError(e));
            }
        }
    );

export const acceptBookingRequestThunk = transitionRequest('transition/accept');
export const declineBookingRequestThunk = transitionRequest('transition/decline');

// Messaging a guest about their booking belongs on that booking's own thread, so it
// reaches them where every other message about it already is.
export const sendBookingMessageThunk = createAsyncThunk(
    'ItineraryPage/sendBookingMessage',
    async ({ txIds, content }, { rejectWithValue, extra: sdk }) => {
        try {
            await Promise.all(
                txIds.map(txId => sdk.messages.send({ transactionId: asUUID(txId), content }))
            );
            return { count: txIds.length };
        } catch (e) {
            return rejectWithValue(storableError(e));
        }
    }
);

/**
 * A guest writing to the host of a session on their itinerary.
 *
 * Two cases, and the guest should not have to know which they are in. Where they have a
 * booking the message goes on that booking's transaction, so it lands where every other
 * message about it already is. Where they have only saved the session there is no
 * transaction at all — nothing has been transacted — so this opens the one Sharetribe
 * keeps for exactly that: an inquiry against the listing, which is a transaction with no
 * booking on it. The listing page's Contact does the same thing from the other end.
 *
 * Returns the transaction the message landed on, so a second message from the same card
 * in the same sitting goes to the thread the first one opened rather than opening another.
 */
export const messageHostThunk = createAsyncThunk(
    'ItineraryPage/messageHost',
    async ({ txId, listing, content }, { rejectWithValue, extra: sdk }) => {
        try {
            if (txId) {
                await sdk.messages.send({ transactionId: asUUID(txId), content });
                return { txId };
            }

            const listingId = asUUID(listing?.id);
            if (!listingId) {
                throw new Error('No listing to open a conversation on');
            }

            /* Which process to open it on. The itinerary holds a snapshot of the listing
               rather than the listing, and what a snapshot carries depends on when it was
               taken — so where it does not say, the listing itself is asked. One extra
               call, and only on the first message about a session nobody has booked. */
            let processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
            if (!processAlias) {
                const shown = await sdk.listings.show({ id: listingId });
                processAlias = shown?.data?.data?.attributes?.publicData?.transactionProcessAlias;
            }
            if (!processAlias) {
                throw new Error('No transaction process attached to listing');
            }
            const [processName] = processAlias.split('/');
            const transitions = getProcess(processName)?.transitions;

            const response = await sdk.transactions.initiate({
                transition: transitions.INQUIRE,
                processAlias,
                params: { listingId },
            });
            const transactionId = response.data.data.id;
            await sdk.messages.send({ transactionId, content });
            return { txId: transactionId };
        } catch (e) {
            return rejectWithValue(storableError(e));
        }
    }
);

// Backward compatible wrapper
export const fetchItineraryEvents = (userId, start, end, isProvider) => (dispatch) => {
    return dispatch(fetchItineraryEventsThunk({ userId, start, end, isProvider })).unwrap();
};

// New action creators for tab-based fetching
export const fetchCustomerEvents = (userId, start, end) => (dispatch) => {
    return dispatch(fetchCustomerEventsThunk({ userId, start, end })).unwrap();
};

export const fetchSellerEvents = (userId, start, end) => (dispatch) => {
    return dispatch(fetchSellerEventsThunk({ userId, start, end })).unwrap();
};

export const fetchProviderDashboard = (userId) => (dispatch) => {
    return dispatch(fetchProviderDashboardThunk({ userId })).unwrap();
};

export const sendBookingMessage = (txIds, content) => (dispatch) => {
    return dispatch(sendBookingMessageThunk({ txIds, content })).unwrap();
};

export const messageHost = ({ txId, listing, content }) => (dispatch) => {
    return dispatch(messageHostThunk({ txId, listing, content })).unwrap();
};

export const acceptBookingRequest = (txId) => (dispatch) => {
    return dispatch(acceptBookingRequestThunk({ txId })).unwrap();
};

// The guest is told why, so the reason travels with the transition rather than being
// kept only on this page.
export const declineBookingRequest = (txId, reason, note) => (dispatch) => {
    const protectedData = reason || note ? { declineReason: reason, declineNote: note } : null;
    return dispatch(declineBookingRequestThunk({ txId, protectedData })).unwrap();
};

// ================ Slice ================ //

const initialState = {
    events: [],
    fetchEventsInProgress: false,
    fetchEventsError: null,
    // provider dashboard
    dashboard: null,
    fetchDashboardInProgress: false,
    fetchDashboardError: null,
    // txId currently being accepted or declined, so only that row shows as busy
    answeringRequestId: null,
    answerRequestError: null,
    // Whether this account has ever posted a listing. Null until asked: the tabs wait
    // for an answer rather than appearing and then disappearing.
    hasEverListed: null,
    /* What the booking being configured in the popup comes to. Cleared while a fresh
       answer is on its way, so a changed seat count never shows the old total beside the
       new number. */
    lineItems: null,
    fetchLineItemsInProgress: false,
    // Which ask the line items on hand are the answer to; see the cases below.
    lineItemsAskedFor: null,
    fetchLineItemsError: null,
};

const itineraryPageSlice = createSlice({
    name: 'ItineraryPage',
    initialState,
    reducers: {
        setInitialValues: (state, action) => {
            return { ...initialState, ...action.payload };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLineItemsThunk.pending, (state, action) => {
                state.fetchLineItemsInProgress = true;
                state.fetchLineItemsError = null;
                /* Dropped rather than kept: these are the figures for the seat count
                   before this one, and showing them under the new number would be the
                   popup quoting a price for an order nobody placed. */
                state.lineItems = null;
                /* And which ask these are to be the answer to. Two can be out together,
                   and the browser answers them in whatever order the network returns, so
                   without this the figures that land last win rather than the figures
                   asked for last. */
                state.lineItemsAskedFor = action.meta?.requestId ?? null;
            })
            .addCase(fetchLineItemsThunk.fulfilled, (state, action) => {
                // Not the answer to the ask on screen: a newer one is on its way, and its
                // figures are the ones that belong under this order.
                if (state.lineItemsAskedFor && state.lineItemsAskedFor !== action.meta?.requestId) return;
                state.fetchLineItemsInProgress = false;
                state.lineItems = action.payload;
            })
            .addCase(fetchLineItemsThunk.rejected, (state, action) => {
                if (state.lineItemsAskedFor && state.lineItemsAskedFor !== action.meta?.requestId) return;
                state.fetchLineItemsInProgress = false;
                state.fetchLineItemsError = action.payload;
            })
            .addCase(fetchItineraryEventsThunk.pending, (state) => {
                state.fetchEventsInProgress = true;
                state.fetchEventsError = null;
            })
            .addCase(fetchItineraryEventsThunk.fulfilled, (state, action) => {
                state.fetchEventsInProgress = false;
                state.events = action.payload;
            })
            .addCase(fetchItineraryEventsThunk.rejected, (state, action) => {
                state.fetchEventsInProgress = false;
                state.fetchEventsError = action.payload;
            })
            .addCase(fetchHasListedThunk.fulfilled, (state, action) => {
                state.hasEverListed = action.payload;
            })
            // A failed lookup leaves it unknown, which shows the guest's half alone. The
            // provider's half would have nothing to put in it either way.
            .addCase(fetchHasListedThunk.rejected, (state) => {
                state.hasEverListed = false;
            })
            .addCase(fetchCustomerEventsThunk.pending, (state) => {
                state.fetchEventsInProgress = true;
                state.fetchEventsError = null;
            })
            .addCase(fetchCustomerEventsThunk.fulfilled, (state, action) => {
                state.fetchEventsInProgress = false;
                state.events = action.payload;
            })
            .addCase(fetchCustomerEventsThunk.rejected, (state, action) => {
                state.fetchEventsInProgress = false;
                state.fetchEventsError = action.payload;
            })
            .addCase(fetchSellerEventsThunk.pending, (state) => {
                state.fetchEventsInProgress = true;
                state.fetchEventsError = null;
            })
            .addCase(fetchSellerEventsThunk.fulfilled, (state, action) => {
                state.fetchEventsInProgress = false;
                state.events = action.payload;
            })
            .addCase(fetchSellerEventsThunk.rejected, (state, action) => {
                state.fetchEventsInProgress = false;
                state.fetchEventsError = action.payload;
            })
            .addCase(fetchProviderDashboardThunk.pending, (state) => {
                state.fetchDashboardInProgress = true;
                state.fetchDashboardError = null;
            })
            .addCase(fetchProviderDashboardThunk.fulfilled, (state, action) => {
                state.fetchDashboardInProgress = false;
                state.dashboard = action.payload;
            })
            .addCase(fetchProviderDashboardThunk.rejected, (state, action) => {
                state.fetchDashboardInProgress = false;
                state.fetchDashboardError = action.payload;
            })
            .addMatcher(
                action => /^ItineraryPage\/transition\//.test(action.type) && action.type.endsWith('/pending'),
                (state, action) => {
                    state.answeringRequestId = action.meta?.arg?.txId || null;
                    state.answerRequestError = null;
                }
            )
            .addMatcher(
                action => /^ItineraryPage\/transition\//.test(action.type) && action.type.endsWith('/fulfilled'),
                (state) => {
                    state.answeringRequestId = null;
                }
            )
            .addMatcher(
                action => /^ItineraryPage\/transition\//.test(action.type) && action.type.endsWith('/rejected'),
                (state, action) => {
                    state.answeringRequestId = null;
                    state.answerRequestError = action.payload;
                }
            );
    },
});

export const { setInitialValues } = itineraryPageSlice.actions;

export default itineraryPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState) => {
    const state = getState();
    const currentUser = state.user?.currentUser;

    // If user is not logged in, we can't fetch their itinerary
    if (!currentUser) {
        return Promise.resolve();
    }

    const { provider: isProvider } = getCurrentUserTypeRoles(config, currentUser);

    // Parse start and end from URL parameters (search string)
    // search is something like "?start=2023-10-01&end=2023-10-31" or parsed object depending on router
    // In this project typical usage, 'search' is the query string or object.
    // We'll assume standard URLSearchParams usage if it's a string, or object if already parsed.

    let start, end;

    // Helper to get params
    const getParam = (s, key) => {
        if (typeof s === 'string') {
            return new URLSearchParams(s).get(key);
        }
        return s[key];
    }

    const startParam = getParam(search, 'start');
    const endParam = getParam(search, 'end');

    if (startParam && endParam) {
        start = new Date(startParam);
        end = new Date(endParam);
    } else {
        // Default to current month if not specified
        start = moment().startOf('month').toDate();
        end = moment().endOf('month').toDate();
    }

    return dispatch(fetchItineraryEvents(currentUser.id.uuid, start, end, isProvider));
};